import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { message } from 'antd';
import { DOLLAR_JOURNAL, UPDATE_DOLLAR_METER_DATA_BULK } from 'graphql/dollarJournalDashboard.gql';
import type { MeterRow, SaveFeedback } from '@custom-types/dollarJournalDashboard';
import useSession from '@hooks/useSession';

export const useDollarJournalDashboardGraphQl = ({ monthYear }: { monthYear?}) => {
  const [tableData, setTableData] = useState<MeterRow[]>([]);
  const [originalData, setOriginalData] = useState<MeterRow[]>([]); // Track original server data
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<MeterRow>>>({});
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback | null>(null);
  const [bulkApprovalSelection, setBulkApprovalSelection] = useState<Set<number>>(new Set());
  const { isAdmin, isManager, isSuperAdmin, isUser, account } = useSession();
  const username = account?.name || account?.username || '';

  // Fetch main data
  const { data, loading, refetch } = useQuery(DOLLAR_JOURNAL, {
    variables: { monthYear },
    fetchPolicy: 'network-only',
  });

  // Initialize table data when data arrives
  useEffect(() => {
    if (data) {
      const rows: MeterRow[] = data.dollarJournal ?? [];
      setTableData(rows);
      setOriginalData(rows); // Store original data from server
      setBulkApprovalSelection(new Set()); // clear on data reload
    }
  }, [data]);

  // Refetch when monthYear changes
  useEffect(() => {
    if (monthYear) {
      refetch({ monthYear });
    }
  }, [monthYear, refetch]);

  const [updateMeterDataBulk, { loading: saving }] = useMutation(UPDATE_DOLLAR_METER_DATA_BULK);

  const handleEdit = (id: number, field: string, value: unknown) => {
    setTableData((prev) =>
      prev.map((row) =>
        row.meter_computed_id === id ? ({ ...row, [field]: value } as MeterRow) : row
      )
    );
    setPendingChanges((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value } as Partial<MeterRow>,
    }));
  };

  const handleSave = async () => {
    const updates = Object.keys(pendingChanges).map((id) => ({
      meter_computed_id: Number(id),
      ...pendingChanges[id],
    }));

    if (!updates.length) {
      message.info('No changes to save.');
      return;
    }

    setSaveFeedback(null);

    try {
      const { data } = await updateMeterDataBulk({ variables: { updates } });
      if (data.updateDollarMeterDataBulk) {
        setPendingChanges({});
        await refetch();
        setSaveFeedback({
          status: 'success',
          message: `Saved ${updates.length} updates successfully!`,
        });
        message.success(`Saved ${updates.length} updates successfully!`);
      } else {
        setSaveFeedback({ status: 'error', message: 'Failed to save updates' });
        message.error('Failed to save updates');
      }
    } catch (err: any) {
      setSaveFeedback({ status: 'error', message: err.message || 'Failed to save updates' });
      message.error(err.message || 'Failed to save updates');
    }
  };

  // Helper function to check if a row was originally approved (from saved server data)
  const isRowOriginallyApproved = (id: number): boolean => {
    const originalRow = originalData.find((row) => row.meter_computed_id === id);
    return Boolean(originalRow?.journal_approved_status);
  };

  // ─── Bulk approval helpers ────────────────────────────────────────────────────

  const bulkApprovableIds = (rows: MeterRow[]): number[] =>
    rows
      .filter(
        (row) =>
          row.journal_ready_for_approval === 'Ready' &&
          !originalData.find((r) => r.meter_computed_id === row.meter_computed_id)
              ?.journal_approved_status
      )
      .map((row) => row.meter_computed_id);

  const getBulkApproveHeaderState = () => {
    const eligible = bulkApprovableIds(tableData);
    if (eligible.length === 0) return { checked: false, indeterminate: false };
    const selectedCount = eligible.filter((id) => bulkApprovalSelection.has(id)).length;
    return {
      checked: selectedCount === eligible.length,
      indeterminate: selectedCount > 0 && selectedCount < eligible.length,
    };
  };

  const handleBulkApproveHeaderChange = (checked: boolean) => {
    const eligible = bulkApprovableIds(tableData);
    if (checked) {
      setBulkApprovalSelection(new Set(eligible));
      setTableData((prev) =>
        prev.map((row) =>
          eligible.includes(row.meter_computed_id)
            ? {
              ...row,
              journal_approved_status: true,
              journal_approver_signature: row.journal_approver_signature || username,
            }
            : row
        )
      );
      const signatureByEligibleId = new Map(
        tableData
          .filter((r) => eligible.includes(r.meter_computed_id))
          .map((r) => [r.meter_computed_id, r.journal_approver_signature || username])
      );
      setPendingChanges((prev) => {
        const next = { ...prev };
        eligible.forEach((id) => {
          next[id] = {
            ...next[id],
            journal_approved_status: true,
            journal_approver_signature: signatureByEligibleId.get(id) ?? username,
          };
        });
        return next;
      });
    } else {
      setBulkApprovalSelection(new Set());
      const originalById = new Map(
        originalData.map((r) => [r.meter_computed_id, r])
      );
      setTableData((prev) =>
        prev.map((row) => {
          if (!eligible.includes(row.meter_computed_id)) return row;
          const original = originalById.get(row.meter_computed_id);
          return original
            ? {
              ...row,
              journal_approved_status: original.journal_approved_status,
              journal_approver_signature: original.journal_approver_signature,
            }
            : row;
        })
      );
      setPendingChanges((prev) => {
        const next = { ...prev };
        eligible.forEach((id) => {
          if (next[id]) {
            delete next[id].journal_approved_status;
            delete next[id].journal_approver_signature;
            if (Object.keys(next[id]).length === 0) delete next[id];
          }
        });
        return next;
      });
    }
  };

  // ─── Bulk "Ready for Approval" helpers (preparer) ────────────────────────────

  // All non-persisted rows — eligible for both check and uncheck
  const bulkReadyEligibleIds = (rows: MeterRow[]): number[] =>
    rows
      .filter((row) => !isRowOriginallyApproved(row.meter_computed_id))
      .map((row) => row.meter_computed_id);

  const getBulkReadyHeaderState = () => {
    const eligible = bulkReadyEligibleIds(tableData);
    if (eligible.length === 0) return { checked: false, indeterminate: false, disabled: true };
    const readyCount = tableData.filter(
      (r) => eligible.includes(r.meter_computed_id) && r.journal_ready_for_approval === 'Ready'
    ).length;
    return {
      checked: readyCount === eligible.length,
      indeterminate: readyCount > 0 && readyCount < eligible.length,
      disabled: false,
    };
  };

  const handleBulkReadyHeaderChange = (checked: boolean) => {
    const eligible = bulkReadyEligibleIds(tableData);
    setTableData((prev) =>
      prev.map((row) =>
        eligible.includes(row.meter_computed_id)
          ? { ...row, journal_ready_for_approval: checked ? 'Ready' : 'In Progress' }
          : row
      )
    );
    setPendingChanges((prev) => {
      const next = { ...prev };
      eligible.forEach((id) => {
        next[id] = { ...next[id], journal_ready_for_approval: checked ? 'Ready' : 'In Progress' };
      });
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────

  const isSaveDisabled = Object.keys(pendingChanges).length === 0;

  return {
    data: tableData,
    loading,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    saving,
    saveFeedback,
    setSaveFeedback,
    handleEdit,
    handleSave,
    refetch,
    isRowOriginallyApproved,
    username,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    bulkApprovalSelection,
    isSaveDisabled,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
  };
};
